import { ApiPath } from '@common/app/api-path.ts';

import {
  type BuyEnergyForDiceGameResponse,
  type CollectCardGameRewardsResponseType,
  type GetGameBoardResponseType,
  type MiniGameDiceRollResponseType,
  type MiniGameExitResponseType,
  type OpenCardsCellResponseType,
  type RollDiceResponseType,
  type SaveAfterBombEventResponseType,
  type StartCardGameResponseType,
} from '@/common/types/dice-game/index.ts';
import { type GamePanelResponse } from '@/common/types/game-center/index.ts';

import { api } from '../base.ts';

import {
  collectCardGameHandler,
  miniGameRollDiceHandler,
  rollDiceHandler,
  safeAfterBombHandler,
} from './query-started-handler.ts';

const diceGameApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getGameBoard: builder.query<GetGameBoardResponseType, void>({
      query: () => ApiPath.GAME_BOARD,
      keepUnusedDataFor: 0,
    }),
    getGamePanel: builder.query<GamePanelResponse, void>({
      query: () => ApiPath.GAME_PANEL,
      keepUnusedDataFor: 0,
    }),
    rollDice: builder.mutation<RollDiceResponseType, number>({
      query: (rollPower) => ({
        url: ApiPath.GAME_ROLL,
        method: 'POST',
        body: { rollPower },
      }),
      onQueryStarted: rollDiceHandler,
    }),
    miniGameRollDice: builder.mutation<MiniGameDiceRollResponseType, void>({
      query: () => ({
        url: ApiPath.MINI_GAME_ROLL,
        method: 'POST',
      }),
      onQueryStarted: miniGameRollDiceHandler,
    }),
    miniGameExit: builder.mutation<MiniGameExitResponseType, void>({
      query: () => ({
        url: ApiPath.MINI_GAME_EXIT,
        method: 'POST',
      }),
    }),
    startCardGame: builder.mutation<StartCardGameResponseType, void>({
      query: () => ({
        url: ApiPath.START_CARD_GAME,
        method: 'POST',
      }),
    }),
    openCardsCell: builder.mutation<
      OpenCardsCellResponseType,
      { cellId: string }
    >({
      query: ({ cellId }) => ({
        url: ApiPath.CARD_GAME_OPEN_CELL + cellId,
        method: 'POST',
      }),
    }),
    saveAfterBombEvent: builder.mutation<SaveAfterBombEventResponseType, void>({
      query: () => ({
        url: ApiPath.CARD_GAME_SAVE_AFTER_BOMB,
        method: 'POST',
      }),
      onQueryStarted: safeAfterBombHandler,
    }),
    collectCardGameRewards: builder.mutation<
      CollectCardGameRewardsResponseType,
      void
    >({
      query: () => ({
        url: ApiPath.CARD_GAME_COLLECT,
        method: 'POST',
      }),
      onQueryStarted: collectCardGameHandler,
    }),
    buyEnergyForDiceGame: builder.mutation<BuyEnergyForDiceGameResponse, void>({
      query: () => ({
        url: ApiPath.GAME_BUY_ENERGY,
        method: 'POST',
      }),
    }),
    // Returns the user's new total dice count after claiming the daily free dice.
    getFreeDailyDice: builder.mutation<number, void>({
      query: () => ({
        url: ApiPath.GAME_FREE_DAILY_DICE,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});
const {
  useGetGameBoardQuery,
  useGetGamePanelQuery,
  useRollDiceMutation,
  useMiniGameExitMutation,
  useMiniGameRollDiceMutation,
  useStartCardGameMutation,
  useOpenCardsCellMutation,
  useSaveAfterBombEventMutation,
  useCollectCardGameRewardsMutation,
  useLazyGetGameBoardQuery,
  useBuyEnergyForDiceGameMutation,
  useGetFreeDailyDiceMutation,
} = diceGameApi;

export {
  diceGameApi,
  useBuyEnergyForDiceGameMutation,
  useCollectCardGameRewardsMutation,
  useGetFreeDailyDiceMutation,
  useGetGameBoardQuery,
  useGetGamePanelQuery,
  useLazyGetGameBoardQuery,
  useMiniGameExitMutation,
  useMiniGameRollDiceMutation,
  useOpenCardsCellMutation,
  useRollDiceMutation,
  useSaveAfterBombEventMutation,
  useStartCardGameMutation,
};
